import { useEffect, useState, useRef } from "react";

const observers = new Map<string, IntersectionObserver>();
const callbacksMap = new Map<Element, (entry: IntersectionObserverEntry) => void>();

type ObserverOptions = {
    threshold?: number;
    root?: Document | Element | null;
};

const getObserver = (threshold = 0.9, root: Document | Element | null = null) => {
    const rootKey = root === null ? "viewport" : root === window.document ? "document" : "element";
    const key = `${threshold}:${rootKey}`;
    let observer = observers.get(key);

    if (!observer) {
        observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    const callback = callbacksMap.get(entry.target);
                    if (callback) {
                        callback(entry);
                    }
                });
            },
            {
                root,
                threshold
            }
        );
        observers.set(key, observer);
    }

    return observer;
};

export const useIntersectionObserver = (
    callback?: (entry: IntersectionObserverEntry) => void,
    options?: ObserverOptions
) => {
    const [isIntersecting, setIsIntersecting] = useState<boolean>(false);
    const elementRef = useRef<any>(null);
    const callbackRef = useRef(callback);
    const wasIntersectingRef = useRef(false);
    const threshold = options?.threshold ?? 0.9;
    const root = options?.root ?? null;
    callbackRef.current = callback;

    useEffect(() => {
        const currentElement = elementRef.current;
        if (!currentElement) return;

        const observerInstance = getObserver(threshold, root);

        callbacksMap.set(currentElement, (entry: IntersectionObserverEntry) => {
            const intersecting = entry.isIntersecting;
            if (intersecting === wasIntersectingRef.current) return;

            wasIntersectingRef.current = intersecting;
            setIsIntersecting(intersecting);

            if (intersecting) {
                callbackRef.current?.(entry);
            }
        });
        observerInstance.observe(currentElement);

        return () => {
            observerInstance.unobserve(currentElement);
            callbacksMap.delete(currentElement);
        };
    }, [threshold, root]);

    return { ref: elementRef, isIntersecting };
};
